"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar } from "lucide-react";
import { useLocale } from "next-intl";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/track";

interface CalendlyEmbedProps {
  url?: string;
  compact?: boolean;
  hideIntroSection?: boolean;
  eager?: boolean;
}

const TIDYCAL_BOOKING_URL =
  "https://tidycal.com/dimensione-4-di-dariomarcobellini/meeting-conoscitivo-30min";

function warmupTidycalConnections(url: string) {
  if (typeof document === "undefined") return;

  if (!document.head.querySelector('link[rel="dns-prefetch"][href="//tidycal.com"]')) {
    const dns = document.createElement("link");
    dns.rel = "dns-prefetch";
    dns.href = "//tidycal.com";
    document.head.appendChild(dns);
  }

  if (!document.head.querySelector('link[rel="preconnect"][href="https://tidycal.com"]')) {
    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = "https://tidycal.com";
    document.head.appendChild(preconnect);
  }

  if (
    !document.head.querySelector(
      `link[rel="prefetch"][href="${url}"]`
    )
  ) {
    const prefetch = document.createElement("link");
    prefetch.rel = "prefetch";
    prefetch.as = "document";
    prefetch.href = url;
    document.head.appendChild(prefetch);
  }
}

export function CalendlyEmbed({
  url = TIDYCAL_BOOKING_URL,
  compact = false,
  hideIntroSection = false,
  eager = true,
}: CalendlyEmbedProps) {
  const locale = useLocale();
  const isItalian = locale.startsWith("it");
  const [isReady, setIsReady] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [showInlineCalendar, setShowInlineCalendar] = useState(eager);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const embedInteractionTrackedRef = useRef(false);
  const embedUrl = useMemo(() => {
    try {
      const normalized = new URL(url);
      normalized.searchParams.set("embed", "1");
      return normalized.toString();
    } catch {
      return url;
    }
  }, [url]);

  const effectiveHideIntroSection = hideIntroSection && !isMobileViewport;
  const cropOffset = effectiveHideIntroSection ? (compact ? 236 : 260) : 0;
  // Desktop: altezza che aderisce al widget TidyCal (~510px). L'overflow-hidden
  // del wrapper taglia il bianco della pagina TidyCal e il footer sotto il widget.
  const wrapperHeight = isMobileViewport
    ? "clamp(640px, 86vh, 920px)"
    : compact
      ? "clamp(556px, 60vh, 576px)"
      : "clamp(556px, 60vh, 576px)";
  const iframeHeight = effectiveHideIntroSection
    ? compact
      ? `calc(clamp(700px, 88vh, 980px) + ${cropOffset}px)`
      : `calc(clamp(760px, 96vh, 1080px) + ${cropOffset}px)`
    : wrapperHeight;

  useEffect(() => {
    warmupTidycalConnections(embedUrl);
  }, [embedUrl]);

  // I click dentro l'iframe TidyCal (cross-origin) non arrivano alla pagina:
  // quando l'utente ci clicca il focus passa all'iframe e la window riceve
  // blur. Traccia la prima interazione col calendario, una volta per mount.
  useEffect(() => {
    const handleWindowBlur = () => {
      window.setTimeout(() => {
        if (embedInteractionTrackedRef.current) return;
        if (!iframeRef.current || document.activeElement !== iframeRef.current) {
          return;
        }
        embedInteractionTrackedRef.current = true;
        trackEvent("tidycal_click", {
          location: "calendar_embed",
          action: "embed_interaction",
        });
      }, 0);
    };
    window.addEventListener("blur", handleWindowBlur);
    return () => window.removeEventListener("blur", handleWindowBlur);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleViewportChange = () => {
      const nextIsMobile = mediaQuery.matches;
      setIsMobileViewport(nextIsMobile);
      setShowInlineCalendar(nextIsMobile ? false : eager);
    };

    handleViewportChange();
    mediaQuery.addEventListener("change", handleViewportChange);
    return () => mediaQuery.removeEventListener("change", handleViewportChange);
  }, [eager]);

  return (
    <div
      id="booking"
      className={`surface-card mx-auto w-full max-w-5xl ${
        compact ? "p-4 sm:p-4 md:p-5" : "p-4 sm:p-5 md:p-8"
      }`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Calendar className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-xl font-semibold">
            {isItalian ? "Prenota una call di 30 minuti" : "Book a 30-minute call"}
          </h3>
          <p className="text-sm text-muted-foreground">
            {isItalian
              ? "Scegli il giorno e l'orario che preferisci"
              : "Choose the day and time you prefer"}
          </p>
        </div>
      </div>

      {isMobileViewport && !showInlineCalendar ? (
        <div className="space-y-3 rounded-lg border border-[hsl(var(--border))] bg-surface-hover p-4">
          <p className="text-sm text-muted-foreground">
            {isItalian
              ? "Su mobile puoi aprire il calendario in fullscreen oppure caricarlo qui."
              : "On mobile you can open the calendar fullscreen or load it inline."}
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Button variant="hero" asChild>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackEvent("tidycal_click", {
                    location: "calendar_embed",
                    action: "open_new_tab",
                  })
                }
              >
                {isItalian ? "Apri calendario" : "Open calendar"}
              </a>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowInlineCalendar(true);
                setIsReady(false);
                setHasLoadError(false);
              }}
            >
              {isItalian ? "Carica qui" : "Load inline"}
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="relative w-full rounded-lg overflow-hidden border border-[hsl(var(--border))] bg-surface-hover"
          style={{ height: wrapperHeight }}
        >
          <iframe
            ref={iframeRef}
            src={embedUrl}
            title={isItalian ? "Prenotazione call TidyCal" : "TidyCal booking"}
            className="w-full rounded-lg overflow-hidden"
            loading={eager ? "eager" : "lazy"}
            scrolling={isMobileViewport ? "yes" : "no"}
            onLoad={() => {
              setIsReady(true);
              setHasLoadError(false);
            }}
            onError={() => {
              setHasLoadError(true);
              setIsReady(false);
            }}
            style={{
              minWidth: "0",
              width: "100%",
              border: 0,
              height: iframeHeight,
              transform: effectiveHideIntroSection
                ? `translateY(-${cropOffset}px)`
                : undefined,
              transformOrigin: "top center",
            }}
          />


          {!isReady && !hasLoadError && (
            <div className="absolute inset-0 bg-surface/80 backdrop-blur-[1px] flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-center px-6">
                <Calendar className="w-8 h-8 text-primary animate-pulse" />
                <p className="text-sm text-muted-foreground">
                  {isItalian ? "Caricamento calendario in corso..." : "Loading calendar..."}
                </p>
              </div>
            </div>
          )}

          {hasLoadError && (
            <div className="absolute inset-x-3 bottom-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {isItalian
                ? "Calendario non disponibile. Aprilo in una nuova scheda."
                : "Calendar unavailable. Open it in a new tab."}
            </div>
          )}
        </div>
      )}

      <noscript>
        <div className="aspect-video bg-surface rounded-lg flex flex-col items-center justify-center gap-4 p-8 text-center">
          <Calendar className="w-12 h-12 text-primary" />
          <p className="text-muted-foreground">
            {isItalian
              ? "Per prenotare una call, visita direttamente la pagina:"
              : "To book a call, open this page directly:"}
          </p>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline font-mono text-sm"
          >
            {url}
          </a>
        </div>
      </noscript>
    </div>
  );
}
